import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { api } from '@/api/repository/axios';
import { designSystem } from '@/styles/theme';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import ThumbnailCropModal, { SAFE_AREA_INSET } from '@/pages/Seminar/components/form/ThumbnailCropModal';
import { GuideLines } from '@/pages/Seminar/components/form/formStyles';

const MAX_FILE_BYTES: number = 10 * 1024 * 1024;

const UploadBox = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  max-width: 360px;
  aspect-ratio: 16 / 9;
  border: 2px dashed #d2d2d2;
  border-radius: 12px;
  background: #f9fafb;
  color: #9ca3af;
  font-size: 14px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${({ disabled }) => (disabled ? '#d2d2d2' : designSystem.colors.primary)};
  }

  &:focus-visible {
    outline: 2px solid ${designSystem.colors.primary};
    outline-offset: 2px;
  }
`;

const PreviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
`;

const PreviewCaption = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 6px;
`;

/** 목록 카드 모양 — 16:9 cover */
const CardPreview = styled.div`
  position: relative;
  width: 180px;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/** 상세 상단 모양 — 16:9 contain + 같은 사진 흐린 배경 */
const HeroPreview = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  background: #111827;

  .blur {
    position: absolute;
    inset: -20px;
    background-size: cover;
    background-position: center;
    filter: blur(18px) brightness(0.8);
  }

  img {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

/** 미리보기 위 안전 영역 점선 */
const SafeAreaGuide = styled.div`
  position: absolute;
  top: ${SAFE_AREA_INSET};
  bottom: ${SAFE_AREA_INSET};
  left: ${SAFE_AREA_INSET};
  right: ${SAFE_AREA_INSET};
  border: 1px dashed rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
  pointer-events: none;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ danger?: boolean }>`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${({ danger }) => (danger ? '#fecaca' : '#d1d5db')};
  background: #fff;
  color: ${({ danger }) => (danger ? '#dc2626' : '#374151')};
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #dc2626;
  white-space: pre-line;
`;

interface SeminarThumbnailFieldProps {
  /** 업로드된 썸네일 URL (없으면 null) */
  value: string | null;
  onChange: (url: string | null) => void;
}

/**
 * 세미나 썸네일 1장 — 목록 카드와 상세 화면이 같은 이미지를 쓴다(모바일 전용 썸네일 없음).
 * 파일을 고르면 16:9 자르기(안전 영역 점선) → 업로드 → 카드·상세 두 모양으로 미리보기.
 */
const SeminarThumbnailField: React.FC<SeminarThumbnailFieldProps> = ({ value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSource, setCropSource] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPicker = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 같은 파일을 다시 골라도 onChange 가 오도록 비운다
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 올릴 수 있어요.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('10MB 이하 이미지만 올릴 수 있어요.');
      return;
    }
    setError(null);
    setCropSource(file);
  };

  const handleCropped = async (file: File) => {
    setCropSource(null);
    setUploading(true);
    try {
      const response = await api.upload.image(file);
      if (response.code === 0 && response.result?.url) {
        onChange(response.result.url);
        setError(null);
      } else {
        throw new Error(response.message || 'upload failed');
      }
    } catch (uploadError) {
      console.error('썸네일 업로드 실패:', uploadError);
      setError('썸네일을 올리지 못했어요.\n잠시 후 다시 시도해 주세요.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {value ? (
        <>
          <PreviewRow>
            <div>
              <PreviewCaption>목록 카드</PreviewCaption>
              <CardPreview>
                <img src={value} alt="목록 카드 썸네일 미리보기" />
              </CardPreview>
            </div>
            <div style={{ flex: '1 1 260px', maxWidth: 400 }}>
              <PreviewCaption>상세 화면</PreviewCaption>
              <HeroPreview>
                <div className="blur" style={{ backgroundImage: `url(${value})` }} />
                <img src={value} alt="상세 화면 썸네일 미리보기" />
                <SafeAreaGuide />
              </HeroPreview>
            </div>
          </PreviewRow>
          <Actions>
            <ActionButton type="button" onClick={openPicker} disabled={uploading}>
              {uploading ? <ButtonSpinner /> : '다른 이미지로 바꾸기'}
            </ActionButton>
            <ActionButton type="button" danger onClick={() => onChange(null)} disabled={uploading}>
              삭제
            </ActionButton>
          </Actions>
        </>
      ) : (
        <UploadBox
          role="button"
          tabIndex={uploading ? -1 : 0}
          aria-label="썸네일 올리기"
          aria-disabled={uploading}
          disabled={uploading}
          onClick={() => {
            if (!uploading) openPicker();
          }}
          // 키보드로도 파일 선택을 열 수 있게 (Enter·Space)
          onKeyDown={(e) => {
            if (uploading) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
        >
          {uploading ? (
            <ButtonSpinner />
          ) : (
            <>
              <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
              <span>썸네일 올리기</span>
            </>
          )}
        </UploadBox>
      )}

      <HiddenInput
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        aria-label="썸네일 이미지 선택"
        onChange={handleFileChange}
      />
      {error && <ErrorText role="alert">{error}</ErrorText>}

      <GuideLines>
        {'16:9(1280×720) 이미지 1장을 올려 주세요.\n글자는 점선 안에 두세요.\n목록 카드와 상세 화면에 같은 이미지가 쓰여요.'}
      </GuideLines>

      <ThumbnailCropModal file={cropSource} onCancel={() => setCropSource(null)} onCropped={handleCropped} />
    </>
  );
};

export default SeminarThumbnailField;
